/**
 * GlobalCTAButtons Component
 * 
 * A reusable, global CTA section displaying "Today's Predictions" and "Betting Tips" buttons.
 * Appears consistently across homepage, coupons, predictions, betting tips, and match pages.
 * 
 * Features:
 * - Mobile-first responsive layout
 * - Consistent styling across all pages
 * - Scroll-to-top behavior on click
 * - Never overlaps footer or fixed UI
 */

import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { TrendingUp, Lightbulb, ChevronRight } from 'lucide-react';
import { scrollToTop } from '@/lib/scrollUtils';

interface GlobalCTAButtonsProps {
  /** Optional className for additional styling */
  className?: string;
  /** Hide specific buttons based on current page */
  hidePredictions?: boolean;
  hideBettingTips?: boolean;
}

export function GlobalCTAButtons({ 
  className = '', 
  hidePredictions = false, 
  hideBettingTips = false 
}: GlobalCTAButtonsProps) {
  const location = useLocation();
  
  // Auto-hide buttons based on current route
  const currentPath = location.pathname;
  const isOnPredictions = currentPath.includes('/predictions');
  const isOnBettingTips = currentPath.includes('/betting-tips');
  
  const showPredictions = !hidePredictions && !isOnPredictions;
  const showBettingTips = !hideBettingTips && !isOnBettingTips;
  
  // Don't render if both buttons are hidden
  if (!showPredictions && !showBettingTips) {
    return null;
  }

  const handleClick = () => {
    // Ensure smooth scroll to top on navigation
    scrollToTop('instant');
  };

  return (
    <section 
      className={`py-6 sm:py-8 ${className}`}
      aria-label="Quick navigation"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
        {showPredictions && (
          <Link 
            to="/predictions/today" 
            onClick={handleClick}
            className="w-full sm:w-auto"
          >
            <Button 
              variant="outline" 
              size="lg"
              className="w-full sm:w-auto gap-2 h-12 sm:h-14 px-6 sm:px-8 text-base font-semibold
                         border-primary/30 hover:border-primary hover:bg-primary/5 
                         transition-all duration-200 group"
            >
              <TrendingUp className="h-5 w-5 text-primary" />
              <span>Today's Predictions</span>
              <ChevronRight className="h-4 w-4 opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all" />
            </Button>
          </Link>
        )}
        
        {showBettingTips && (
          <Link 
            to="/betting-tips" 
            onClick={handleClick}
            className="w-full sm:w-auto"
          >
            <Button 
              variant="outline" 
              size="lg"
              className="w-full sm:w-auto gap-2 h-12 sm:h-14 px-6 sm:px-8 text-base font-semibold
                         border-green-500/30 hover:border-green-500 hover:bg-green-500/5 
                         transition-all duration-200 group"
            >
              <Lightbulb className="h-5 w-5 text-green-600" />
              <span>Betting Tips</span>
              <ChevronRight className="h-4 w-4 opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all" />
            </Button>
          </Link>
        )}
      </div>
    </section>
  );
}
